var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
/** @jsxRuntime classic */

/** @jsx jsx */
// @ts-check


import { Formik, useFormikContext } from 'formik';
import { jsx } from 'theme-ui';
import { useFormTheme } from '.';
import { FormThemeProvider } from './useFormTheme';
/**
 * Renders a form via Formik that is validated using a yup schema.
 *
 * @template {import('formik').FormikValues} Values
 * @param {import('formik').FormikConfig<Values>
 *    & Required<Pick<import('formik').FormikConfig<Values>, 'validationSchema'>>
 *    & {
 *      theme?: import('./useFormTheme').FormThemeVariant | import('./useFormTheme').FormTheme;
 *    }
 *    & HtmlFormProps
 * } props
 * @returns
 */

export default function Form(_a) {
  var initialValues = _a.initialValues,
      validationSchema = _a.validationSchema,
      onSubmit = _a.onSubmit,
      children = _a.children,
      _a$theme = _a.theme,
      theme = _a$theme === void 0 ? 'classic' : _a$theme,
      props = __rest(_a, ["initialValues", "validationSchema", "onSubmit", "children", "theme"]); // Extract all Formik props and leave only the rest for the HTML form element.


  var component = props.component,
      enableReinitialize = props.enableReinitialize,
      initialErrors = props.initialErrors,
      initialStatus = props.initialStatus,
      initialTouched = props.initialTouched,
      innerRef = props.innerRef,
      isInitialValid = props.isInitialValid,
      onReset = props.onReset,
      render = props.render,
      validate = props.validate,
      validateOnBlur = props.validateOnBlur,
      validateOnChange = props.validateOnChange,
      validateOnMount = props.validateOnMount,
      formProps = __rest(props, ["component", "enableReinitialize", "initialErrors", "initialStatus", "initialTouched", "innerRef", "isInitialValid", "onReset", "render", "validate", "validateOnBlur", "validateOnChange", "validateOnMount"]);

  return jsx(Formik, {
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: onSubmit,
    render: render,
    onReset: onReset,
    component: component,
    enableReinitialize: enableReinitialize,
    initialErrors: initialErrors,
    initialStatus: initialStatus,
    initialTouched: initialTouched,
    innerRef: innerRef,
    isInitialValid: isInitialValid,
    validate: validate,
    validateOnBlur: validateOnBlur,
    validateOnChange: validateOnChange,
    validateOnMount: validateOnMount
  }, jsx(FormThemeProvider, {
    value: theme
  }, jsx(HtmlForm, Object.assign({}, formProps), children)));
}
/**
 * @typedef {Omit<import('react').FormHTMLAttributes, 'onSubmit' | 'onReset'>} HtmlFormProps
 */

/**
 * This reimplements the formik.Form component because I couldn't extract the typings for formik's implementation correctly.
 */

function HtmlForm(_a) {
  var children = _a.children,
      rest = __rest(_a, ["children"]);

  var _useFormikContext = useFormikContext(),
      handleReset = _useFormikContext.handleReset,
      handleSubmit = _useFormikContext.handleSubmit;

  var theme = useFormTheme();
  return jsx("form", Object.assign({
    sx: theme === null || theme === void 0 ? void 0 : theme.form
  }, rest, {
    onReset: handleReset,
    onSubmit: handleSubmit
  }), children);
}